/**
 * @file     vowOptIn.ts
 * @readme   ./README.md
 * @purpose  The lead-side half of a flow's account capture, mirroring VowAccessModal: the user-signup lead after the submit is accepted, the VOW partial when the visitor leaves mid-step, the parked phone that lets an interrupted registration resume pre-filled, and the call-request answer key and tag.
 */
'use client';

import { VOW_PENDING_PHONE_KEY } from '@/components/vow/useVowRegistrationResume';
import { vowPartialLeadType, vowSignupFlowId, vowSignupLeadType } from '@/components/vow/vowSignupLeadType';
import { splitFullName } from '@/services/leads/contactFields';
import { toE164 } from '@/utils/formatPhone';

/** Answer key for the optional call request on the account step. */
export const CALL_REQUEST_ANSWER_KEY = 'callRequested';
/** FUB tag a call request adds to the lead. */
export const CALL_REQUESTED_TAG = 'Call requested';

type ContactDetails = {
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string;
  sourcePage: string;
  elapsedSeconds: number;
};

/** First and last travel separately; callers that only hold a full name get it split. */
function nameParts(details: ContactDetails): { name: string; firstName: string; lastName: string } {
  const split = splitFullName(details.name);
  const firstName = details.firstName?.trim() || split.firstName;
  const lastName = details.lastName?.trim() || split.lastName;
  const name = details.name.trim() || [firstName, lastName].filter(Boolean).join(' ');
  return { name, firstName, lastName };
}

export function parkVowPhone(phone: string): void {
  try {
    if (phone.trim()) window.localStorage.setItem(VOW_PENDING_PHONE_KEY, phone.trim());
  } catch {
    // Private mode or storage disabled: the resume path just asks for the phone again.
  }
}

/**
 * Every signup surface files a user-signup lead (the VOW gate signup
 * pipeline rule). The account submit already reported the page's conversion,
 * so this one never fires a second Ads or GA4 conversion.
 */
export async function fileVowSignupLead(details: ContactDetails): Promise<void> {
  const { submitLead } = await import('@/services/leads/submitLead');
  const type = vowSignupLeadType(details.sourcePage);
  await submitLead(
    {
      type,
      ...nameParts(details),
      email: details.email,
      phone: toE164(details.phone) ?? details.phone.trim(),
      sourcePage: details.sourcePage,
      antiSpam: {
        honeypotFilled: false,
        elapsedSeconds: details.elapsedSeconds,
        submissionTooFast: false,
      },
    },
    { sourcePage: details.sourcePage, flowId: vowSignupFlowId(type), trackConversion: false },
  );
}

/**
 * The visitor left the account step with an email typed: keep the lead, flagged as
 * a partial. Reports its conversion like the VowAccessModal partial does,
 * since no seller conversion fired for this visitor.
 */
export async function fileVowPartialLead(details: ContactDetails): Promise<void> {
  const { submitLead } = await import('@/services/leads/submitLead');
  await submitLead(
    {
      type: vowPartialLeadType(details.sourcePage),
      ...nameParts(details),
      email: details.email,
      // A half-typed number would fail the route's phone check and lose the
      // partial, so only a complete one travels.
      phone: toE164(details.phone) ?? '',
      sourcePage: details.sourcePage,
      antiSpam: {
        honeypotFilled: false,
        elapsedSeconds: details.elapsedSeconds,
        submissionTooFast: false,
      },
    },
    { sourcePage: details.sourcePage, flowId: 'vowPartial' },
  );
}
